import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Renders product variant metafields (custom.infusion_herbs, custom.ingredients_list)
 * on the product page and re-syncs whenever the buyer picks a different variant.
 *
 * Strategy: variant-picker dispatches `variant:update` with the freshly-fetched
 * section HTML in `event.detail.data.html`. Liquid already re-renders this block
 * for the new variant inside that HTML, so we just locate the matching block by
 * id and swap our content in.
 */
class VariantMetafieldsComponent extends Component {
  /** @type {HTMLElement | null} */
  #target = null;

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    if (!target) return;
    this.#target = target;
    target.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#target) {
      this.#target.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
      this.#target = null;
    }
  }

  /**
   * @param {CustomEvent<{ data?: { html?: Document } }>} event
   */
  handleVariantUpdate = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const next = html.querySelector(
      `variant-metafields-component[data-block-id="${CSS.escape(blockId)}"]`
    );
    if (!next) return;

    const nextContent = next.querySelector('.variant-metafields__content');
    const currentContent = this.querySelector('.variant-metafields__content');
    if (nextContent && currentContent) {
      currentContent.innerHTML = nextContent.innerHTML;
    }

    this.classList.toggle(
      'variant-metafields--empty',
      next.classList.contains('variant-metafields--empty')
    );
  };
}

if (!customElements.get('variant-metafields-component')) {
  customElements.define('variant-metafields-component', VariantMetafieldsComponent);
}
